// The OTLP telemetry plugin's repo-settings page — the per-repo live-trace
// source config (the OTel backend the board's request-flow overlay streams
// from). Contributed into the SettingsModal's `repo` scope via `settingsPages`;
// reads the active slug/repo from the host ctx. Moved here from RepoSettings.tsx
// so the config is the telemetry feature-module's own client half (a single
// trace-config surface), not a hardcoded settings tab.

import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { WorkbenchHostContext } from "@archlang/app-shell/board";
import { apiFetch } from "../../../api/client";
import { useWorkspace } from "../../../api/hooks/useWorkspace";
import { Spinner } from "../../../ui/Spinner";
import { Banner } from "../../../ui/Banner";
import { Field } from "../../../ui/Field";
import { TextInput } from "../../../ui/TextInput";
import { Button } from "../../../ui/Button";
import { useToast } from "../../../ui/Toast";
import { useTranslation, apiErrorMessage } from "../../../i18n";

interface ServiceMapEntry {
  service: string;
  module: string;
}

interface TraceSourceConfig {
  kind: "jaeger" | "tempo";
  endpoint: string;
  metricsEndpoint?: string | null;
  authHeader?: string | null;
  metricsAuthHeader?: string | null;
  serviceMap?: ServiceMapEntry[];
  lookbackSec: number;
  enabled: boolean;
}

/** service→module map ⇆ textarea: one `service = module` per line. */
function serviceMapToText(map: ServiceMapEntry[] | undefined): string {
  return (map ?? []).map((e) => `${e.service} = ${e.module}`).join("\n");
}
function textToServiceMap(text: string): ServiceMapEntry[] {
  const out: ServiceMapEntry[] = [];
  for (const line of text.split("\n")) {
    const i = line.indexOf("=");
    if (i < 0) continue;
    const service = line.slice(0, i).trim();
    const module = line.slice(i + 1).trim();
    if (service && module) out.push({ service, module });
  }
  return out;
}

/** Per-repo live-trace source config, keyed by the active repo. repo:admin
 *  (owner) to edit. */
function TraceSourceForm({ slug, repo }: { slug: string; repo: string }) {
  const { t: tErr } = useTranslation("errors");
  const toast = useToast();
  const qc = useQueryClient();
  const isOwner = useWorkspace(slug).data?.role === "owner";
  const key = ["repo-trace-source", slug, repo];
  const query = useQuery({
    queryKey: key,
    queryFn: () =>
      apiFetch<{ traceSource: TraceSourceConfig | null }>(
        `/api/ws/${encodeURIComponent(slug)}/r/${encodeURIComponent(repo)}/trace-source`,
      ),
  });

  const [endpoint, setEndpoint] = useState("");
  const [kind, setKind] = useState<"jaeger" | "tempo">("jaeger");
  const [enabled, setEnabled] = useState(true);
  const [metricsEndpoint, setMetricsEndpoint] = useState("");
  const [authHeader, setAuthHeader] = useState("");
  const [metricsAuthHeader, setMetricsAuthHeader] = useState("");
  const [serviceMapText, setServiceMapText] = useState("");
  useEffect(() => {
    const c = query.data?.traceSource;
    if (c) {
      setEndpoint(c.endpoint);
      setKind(c.kind);
      setEnabled(c.enabled);
      setMetricsEndpoint(c.metricsEndpoint ?? "");
      setAuthHeader(c.authHeader ?? "");
      setMetricsAuthHeader(c.metricsAuthHeader ?? "");
      setServiceMapText(serviceMapToText(c.serviceMap));
    }
  }, [query.data]);

  const save = useMutation({
    mutationFn: (body: Partial<TraceSourceConfig>) =>
      apiFetch<{ traceSource: TraceSourceConfig }>(
        `/api/ws/${encodeURIComponent(slug)}/r/${encodeURIComponent(repo)}/trace-source`,
        { method: "PUT", body },
      ),
    onSuccess: (data) => {
      toast.success("Live-traffic source saved");
      qc.setQueryData(key, data);
    },
    onError: (err: Error) => toast.error(apiErrorMessage(err, tErr)),
  });

  if (query.isLoading) {
    return (
      <div className="flex items-center gap-2 p-2 text-sm text-slate-400">
        <Spinner /> Loading…
      </div>
    );
  }
  if (query.error) {
    return <Banner kind="error">{apiErrorMessage(query.error, tErr)}</Banner>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-slate-100">Live traffic</h2>
        <p className="mt-1 text-xs text-slate-400">
          Stream request flow onto the board from an OpenTelemetry trace backend
          (Jaeger query API or Tempo). The board animates each edge by its live
          request rate.
        </p>
      </div>
      <div className="space-y-4">
        <Field label="Backend">
          {({ id }) => (
            <select
              id={id}
              value={kind}
              onChange={(e) => setKind(e.target.value as "jaeger" | "tempo")}
              disabled={!isOwner}
              className="w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-200"
            >
              <option value="jaeger">Jaeger</option>
              <option value="tempo">Tempo</option>
            </select>
          )}
        </Field>
        <Field label="Endpoint">
          {({ id }) => (
            <TextInput
              id={id}
              value={endpoint}
              onChange={(e) => setEndpoint(e.target.value)}
              placeholder={kind === "tempo" ? "http://tempo:3200" : "http://jaeger:16686"}
              disabled={!isOwner}
            />
          )}
        </Field>
        {kind === "tempo" && (
          <Field label="Metrics endpoint (Prometheus)">
            {({ id }) => (
              <TextInput
                id={id}
                value={metricsEndpoint}
                onChange={(e) => setMetricsEndpoint(e.target.value)}
                placeholder="http://prometheus:9090"
                disabled={!isOwner}
              />
            )}
          </Field>
        )}
        <p className="-mt-2 text-[11px] text-slate-500">
          {kind === "tempo"
            ? "With a metrics endpoint, latency percentiles + error rate come from the metrics-generator histograms (accurate). Without it, they're sampled from raw traces."
            : "Latency + error rate are sampled from raw traces. Jaeger SPM metrics can sharpen them where available."}
        </p>
        <Field label="Auth header (optional)">
          {({ id }) => (
            <TextInput
              id={id}
              value={authHeader}
              onChange={(e) => setAuthHeader(e.target.value)}
              placeholder="Bearer …"
              disabled={!isOwner}
            />
          )}
        </Field>
        {kind === "tempo" && (
          <Field label="Metrics auth header (optional)">
            {({ id }) => (
              <TextInput
                id={id}
                value={metricsAuthHeader}
                onChange={(e) => setMetricsAuthHeader(e.target.value)}
                placeholder="Bearer …"
                disabled={!isOwner}
              />
            )}
          </Field>
        )}
        <Field label="Service → module map (optional)">
          {({ id }) => (
            <textarea
              id={id}
              value={serviceMapText}
              onChange={(e) => setServiceMapText(e.target.value)}
              placeholder={"checkout-svc = Checkout\npayments-prod = Payments"}
              disabled={!isOwner}
              rows={3}
              className="w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 font-mono text-xs text-slate-200"
            />
          )}
        </Field>
        <p className="-mt-2 text-[11px] text-slate-500">
          One <code>service = module</code> per line. Overrides the name-matching
          when observed service names differ from the model.
        </p>
        <label className="flex items-center gap-3">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            disabled={!isOwner}
            className="h-4 w-4 rounded border-slate-600 bg-slate-900 text-arch-500"
          />
          <span className="text-sm text-slate-200">Enabled</span>
        </label>
        <div className="flex justify-end">
          <Button
            type="button"
            variant="primary"
            disabled={!isOwner || save.isPending || !endpoint.trim()}
            onClick={() =>
              save.mutate({
                kind,
                endpoint: endpoint.trim(),
                metricsEndpoint: metricsEndpoint.trim() || null,
                authHeader: authHeader.trim() || null,
                metricsAuthHeader: metricsAuthHeader.trim() || null,
                serviceMap: textToServiceMap(serviceMapText),
                enabled,
              })
            }
          >
            {save.isPending ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>
    </div>
  );
}

/** The contributed settings Page — resolves the active repo from the host ctx and
 *  renders the trace-source form. Self-hides (renders nothing) with no active
 *  repo, mirroring the page's `when(ctx)` gate. */
export function TraceSourcePage({ ctx }: { ctx: WorkbenchHostContext }) {
  const repo = ctx.activeRepo;
  if (!repo) return null;
  return <TraceSourceForm slug={repo.slug} repo={repo.repo} />;
}
